import { z } from 'zod';

import { LocalTimeSchema } from './datetime';
import { ShiftTemplateId, ShiftTemplateVersionId } from './ids';

/**
 * 008 · tipos compartidos de horarios y tareo (contracts/admin-api.md · "Tipos compartidos").
 *
 * Los usan el realm admin (`admin/schedules.ts`, `admin/absences.ts`, `admin/timesheet.ts`,
 * `admin/justifications.ts`) y el realm staff (`staff/schedule.ts`, `staff/justifications.ts`).
 * Los clientes eligen textos con `…Schema.enum`, nunca comparando literales.
 */

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** Días máximos del cuadro de turnos por consulta (FR-006). */
export const SCHEDULE_GRID_MAX_DAYS = 42;
/** Celdas máximas por guardado del cuadro (una transacción, todo o nada). */
export const SCHEDULE_GRID_MAX_CELLS = 500;
/** Días máximos de la vista del tareo (FR-019). */
export const TIMESHEET_VIEW_MAX_DAYS = 31;
/** Días máximos de una descarga del tareo (FR-021, research R-21). */
export const TIMESHEET_EXPORT_MAX_DAYS = 62;
/** Largo máximo del ciclo de un patrón. */
export const PATTERN_MAX_CYCLE_DAYS = 56;
/** Trabajadores máximos por aplicación de un patrón. */
export const PATTERN_APPLY_MAX_WORKERS = 200;
/** Justificaciones por trabajador, día y tipo (contando canceladas y rechazadas). */
export const JUSTIFICATION_MAX_PER_DAY_KIND = 3;
/** Largo máximo del motivo de una justificación (el mismo tope que la regularización de la 007). */
export const JUSTIFICATION_REASON_MAX = 300;
/** Duración mínima y máxima de un turno, en minutos. */
export const SHIFT_MIN_DURATION_MINUTES = 60;
export const SHIFT_MAX_DURATION_MINUTES = 1439;
/** Días máximos de una ausencia (`end_day − start_day`, mismo `CHECK` que `worker_absences`). */
export const ABSENCE_MAX_SPAN_DAYS = 366;

// ---------------------------------------------------------------------------
// Utilidades de fechas locales (puras, sin zona)
// ---------------------------------------------------------------------------

/**
 * Diferencia en días entre dos `LocalDate` (`YYYY-MM-DD`): `to − from`. Negativa si `to < from`.
 * Se calcula sobre medianoches UTC, así que no depende de la zona del proceso.
 */
export function localDayDiff(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Minutos desde medianoche de un `LocalTime` (`HH:mm`). */
export function localTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/**
 * Duración de un turno en minutos. `end ≤ start` ⇒ cruza medianoche (termina al día siguiente).
 * `start = end` devuelve 0 (turno inválido: la validación lo rechaza aparte).
 */
export function shiftDurationMinutes(startTime: string, endTime: string): number {
  const start = localTimeToMinutes(startTime);
  const end = localTimeToMinutes(endTime);
  if (end === start) return 0;
  return end > start ? end - start : end + 1440 - start;
}

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Origen de un dato de horarios (`schedule_source`). `INTEGRATION` es de solo lectura en el Admin. */
export const ScheduleSourceSchema = z.enum(['MANUAL', 'INTEGRATION']);
export type ScheduleSource = z.infer<typeof ScheduleSourceSchema>;

/** Tipo de plan de un día (`day_plan_kind`). `OFF` = sin turno (p. ej. fin de un patrón). */
export const DayPlanKindSchema = z.enum(['SHIFT', 'REST', 'OFF']);
export type DayPlanKind = z.infer<typeof DayPlanKindSchema>;

/** Tipo de ausencia (`absence_type`). Solo `PERMISSION` admite horas (ausencia parcial). */
export const AbsenceTypeSchema = z.enum(['PERMISSION', 'VACATION', 'LICENSE', 'MEDICAL_LEAVE']);
export type AbsenceType = z.infer<typeof AbsenceTypeSchema>;

/**
 * Estado del día (`day_status` + `PENDING`). `PENDING` no se persiste: es el día aún no final,
 * que solo existe en vivo y en los contratos.
 */
export const DayStatusSchema = z.enum([
  'PRESENT',
  'ABSENT',
  'REST',
  'HOLIDAY',
  'ABSENCE',
  'UNSCHEDULED',
  'NO_SCHEDULE',
  'PENDING',
]);
export type DayStatus = z.infer<typeof DayStatusSchema>;

/** Señales de la evaluación (`evaluation_flag`). */
export const EvaluationFlagSchema = z.enum([
  'LATE',
  'EARLY_LEAVE',
  'INCOMPLETE',
  'CHECKOUT_WITHOUT_CHECKIN',
  'OTHER_SITE',
  'OUT_OF_SHIFT_MARKS',
  'PARTIAL_ABSENCE',
  'PARTIAL_PERMISSION',
  'ON_HOLIDAY',
  'ON_REST',
  'WORKED_DURING_ABSENCE',
  'LATE_JUSTIFIED',
  'EARLY_LEAVE_JUSTIFIED',
  'ABSENCE_JUSTIFIED',
]);
export type EvaluationFlag = z.infer<typeof EvaluationFlagSchema>;

/** Estado de presentación (derivado por `displayStatusOf`; no es SQL). */
export const DayDisplayStatusSchema = z.enum([
  'ON_TIME',
  'LATE',
  'EARLY_LEAVE',
  'LATE_AND_EARLY_LEAVE',
  'INCOMPLETE',
  'ABSENT',
  'ABSENT_JUSTIFIED',
  'UNSCHEDULED_WORK',
  'REST',
  'HOLIDAY',
  'PERMISSION',
  'VACATION',
  'LICENSE',
  'MEDICAL_LEAVE',
  'NO_SCHEDULE',
  'PENDING',
]);
export type DayDisplayStatus = z.infer<typeof DayDisplayStatusSchema>;

/** Código corto de cada estado en la matriz del tareo y en el Excel. */
export const TIMESHEET_CODES: Readonly<Record<DayDisplayStatus, string>> = Object.freeze({
  ON_TIME: 'A',
  LATE: 'T',
  EARLY_LEAVE: 'S',
  LATE_AND_EARLY_LEAVE: 'TS',
  INCOMPLETE: 'I',
  ABSENT: 'F',
  ABSENT_JUSTIFIED: 'FJ',
  UNSCHEDULED_WORK: 'MS',
  REST: 'D',
  HOLIDAY: 'FE',
  PERMISSION: 'P',
  VACATION: 'V',
  LICENSE: 'L',
  MEDICAL_LEAVE: 'DM',
  NO_SCHEDULE: 'SH',
  PENDING: '·',
});

/** Tipo de justificación (`justification_kind`). */
export const JustificationKindSchema = z.enum(['LATE', 'EARLY_LEAVE', 'ABSENCE']);
export type JustificationKind = z.infer<typeof JustificationKindSchema>;

/** Estado de una justificación (`justification_status`). */
export const JustificationStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']);
export type JustificationStatus = z.infer<typeof JustificationStatusSchema>;

/** Motivo de cancelación (`justification_cancel_reason`). */
export const JustificationCancelReasonSchema = z.enum([
  'WORKER',
  'NO_LONGER_APPLIES',
  'WORKER_DEACTIVATED',
]);
export type JustificationCancelReason = z.infer<typeof JustificationCancelReasonSchema>;

/** Origen de una justificación (`justification_origin`): pedida por el trabajador o registrada por el admin. */
export const JustificationOriginSchema = z.enum(['WORKER', 'ADMIN']);
export type JustificationOrigin = z.infer<typeof JustificationOriginSchema>;

/**
 * Layouts del Excel del tareo (research R-19). `companies.timesheet_layout` no tiene `CHECK`: el
 * catálogo vive aquí y el registro de layouts de la API es `Record<TimesheetLayoutId, …>`.
 */
export const TIMESHEET_LAYOUTS = ['STANDARD'] as const;
export const TimesheetLayoutSchema = z.enum(TIMESHEET_LAYOUTS);
export type TimesheetLayoutId = z.infer<typeof TimesheetLayoutSchema>;

// ---------------------------------------------------------------------------
// Resúmenes
// ---------------------------------------------------------------------------

/** Un turno tal como se aplicó a un día (versión de plantilla resuelta). */
export const ShiftSummarySchema = z.object({
  template_id: ShiftTemplateId,
  template_version_id: ShiftTemplateVersionId,
  name: z.string(),
  code: z.string(),
  start_time: LocalTimeSchema,
  end_time: LocalTimeSchema,
  crosses_midnight: z.boolean(),
  break_minutes: z.number().int().nonnegative(),
});
export type ShiftSummary = z.infer<typeof ShiftSummarySchema>;

/** Resultado de la evaluación de un día (vivo o persistido). */
export const EvaluationSummarySchema = z.object({
  status: DayStatusSchema,
  display_status: DayDisplayStatusSchema,
  /** `TIMESHEET_CODES[display_status]`. */
  code: z.string(),
  /** Tipo de la ausencia que explica el día (`status = 'ABSENCE'`); si no, `null`. */
  absence_type: AbsenceTypeSchema.nullable(),
  late_minutes: z.number().int().nonnegative(),
  early_leave_minutes: z.number().int().nonnegative(),
  worked_minutes: z.number().int().nonnegative(),
  scheduled_minutes: z.number().int().nonnegative(),
  /** El día ya no puede cambiar por el paso del tiempo (solo por correcciones). */
  final: z.boolean(),
  flags: z.array(EvaluationFlagSchema),
});
export type EvaluationSummary = z.infer<typeof EvaluationSummarySchema>;
